import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { IValidateCoveredZone } from "@/apps/credit-line/models/IValidateCoveredZone";
import { Service } from "@/apps/credit-line/services/network.service";
import { IValidateShippingCoverageZoneRequest } from "@/apps/credit-line/types/requests";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { LogType } from "@/types/dataDogLogType";
import { reactive } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";

interface useValidateShippingCoverageReturnType {
  zoneWithCoverage: {
    hasCoverage: boolean;
    isLoading: boolean;
    error: string;
  };
  validateCoverage: (
    params: IValidateShippingCoverageZoneRequest
  ) => Promise<IValidateCoveredZone | undefined>;
}

export function useValidateShippingCoverage(): useValidateShippingCoverageReturnType {
  const { t } = useI18n();
  const zoneWithCoverage = reactive({
    hasCoverage: false,
    isLoading: false,
    error: "",
  });
  const CreditLineService = new Service();

  const validateCoverage = async (
    params: IValidateShippingCoverageZoneRequest
  ) => {
    try {
      zoneWithCoverage.isLoading = true;
      zoneWithCoverage.error = "";
      const res = await CreditLineService.validateCoverageZone(params);
      zoneWithCoverage.hasCoverage = res?.data?.isZoneWithCoverage;
      zoneWithCoverage.isLoading = false;

      const doesNotHaveRegionCoverage = res?.data?.error?.includes(
        "covered zone not found"
      );

      const doesNotHaveVendorCoverage = res?.data?.error?.includes(
        "does not have vendors in this zone"
      );

      if (doesNotHaveRegionCoverage && !res?.data?.isZoneWithCoverage) {
        zoneWithCoverage.error = t(
          "PAYJOY_CARD_VIEWS.ADDRESS_FORM_COVERAGE_ERROR"
        );
      } else if (doesNotHaveVendorCoverage && !res?.data?.isZoneWithCoverage) {
        zoneWithCoverage.error = t(
          "PAYJOY_CARD_VIEWS.ADDRESS_FORM_NO_COVERAGE"
        );
      }

      return res?.data;
    } catch (e: unknown) {
      zoneWithCoverage.error = t(
        "PAYJOY_CARD_VIEWS.ADDRESS_FORM_COVERAGE_ERROR"
      );
      DatadogLogger.Log(
        "Error validating shipping zip code",
        {
          deviceTag: AuthenticationHelper.getDeviceTag(),
          error: e,
        },
        LogType.ERROR
      );

      if (e && typeof e === "object" && "response" in e) {
        const errorObj = e as { response?: { data?: { errorCode?: unknown } } };

        if (errorObj.response && !errorObj.response.data?.errorCode) {
          DatadogLogger.Log(
            "Error validating shipping zip code network error",
            {
              deviceTag: AuthenticationHelper.getDeviceTag(),
              error: e,
            },
            LogType.ERROR
          );
        }
      }
    }
  };

  return {
    zoneWithCoverage,
    validateCoverage,
  };
}
